import { createTheme } from "@mui/material";
import { FiChevronDown } from "react-icons/fi";
import { fluidTypography } from "./utils/responsive";

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 1000,
      lg: 1200,
      xl: 1920,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0px 23px 44px 0 rgba(176, 183, 195, 0.22), 0 1px 3px 0 rgba(0, 0, 0, 0.15)",
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { variant: "stats-up" },
          style: {
            backgroundColor: "#E8F5E9",
            color: "#388E3C",
            fontSize: 12,
            padding: "0 4px",
            "& .MuiChip-label": {
              color: "#388E3C",
              fontSize: 12,
              fontWeight: 500,
            },
            "& .MuiChip-icon": {
              fontSize: 14,
              color: "#388E3C",
            },
          },
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: {
            variant: "white-contained",
          },
          style: {
            backgroundColor: "white",
            borderRadius: "6px",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.12)",
            color: "#030712",
            padding: "12px 1rem",
            minHeight: 46,
            "&: hover": {
              backgroundColor: "white",
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.12)",
            },
            "&:focus": {
              backgroundColor: "white",
              boxShadow:
                "0 0 0 4px rgba(28, 180, 186, 0.24), 0 1px 2px 0 rgba(78, 78, 78, 0.5)",
            },
          },
        },
        {
          props: {
            variant: "primary-contained",
          },
          style: {
            backgroundColor: "#0B494B",
            borderRadius: "6px",
            boxShadow: "0 1px 2px 0 #1018280D",
            color: "white",
            padding: "12px 1rem",
            minHeight: 46,
            "&:hover": {
              backgroundColor: "#093B3D",
              boxShadow: "0 1px 2px 0 #1018280D",
            },
            "&:focus": {
              backgroundColor: "#0B494B",
              boxShadow:
                "0 0 0 4px rgba(28, 180, 186, 0.24), 0 1px 2px 0 rgba(78, 78, 78, 0.5)",
            },
            "&.Mui-disabled": {
              color: "#fff",
            },
          },
        },
        {
          props: {
            variant: "white-outlined",
          },
          style: {
            backgroundColor: "#FFFFFF",
            borderRadius: "6px",
            boxShadow: "0 1px 2px 0 #1018280D",
            padding: "12px 1rem",
            border: "1px solid #E6E7E7",
            color: "#030712",
            fontSize: 12,
            fontWeight: 500,
            "&: hover": {
              boxShadow: "0 1px 2px 0 #1018280D",
            },
            "&:focus": {
              boxShadow:
                "0 0 0 4px rgba(28, 180, 186, 0.24), 0 1px 2px 0 rgba(78, 78, 78, 0.5)",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "6px",
        },
        contained: {
          boxShadow: "none",
        },
        outlined: {
          border: "1px solid #E6E7E7",
          boxShadow: "unset",
          borderRadius: "32px",
          padding: "3px 20px",
          width: "fit-content",
          backgroundColor: "transparent",
          borderColor: "unset",
        },
        sizeSmall: {
          padding: "6px 16px",
        },
        sizeMedium: {
          padding: "8px 20px",
        },
        sizeLarge: {
          padding: "9px 21px",
        },
        textSizeSmall: {
          padding: "7px 12px",
        },
        textSizeMedium: {
          padding: "9px 16px",
        },
        textSizeLarge: {
          padding: "12px 16px",
        },
        outlinedPrimary: {
          borderColor: "#111827",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "32px 24px",
          "&:last-child": {
            paddingBottom: "32px",
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
        },
        subheaderTypographyProps: {
          variant: "body2",
        },
      },
      styleOverrides: {
        root: {
          padding: "32px 24px",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
          margin: 0,
          padding: 0,
        },
        html: {
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
        },
        body: {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
          backgroundColor: "#f7f8f8",
        },
        "#__next": {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "#8D8D8D",
          "&.Mui-selected": {
            color: "#1cb4ba",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: "11.5px 14px",
          "&::placeholder": {
            color: "#aeb3b3",
            letterSpacing: "-1%",
            fontSize: "16px",
            fontWeight: 400,
            opacity: 1,
          },
        },
        root: {
          fontSize: "16px",
          fontWeight: 500,
          color: "#030712",
          lineHeight: "160%",
        },
        notchedOutline: {
          borderColor: "#e6e6e9",
          borderRadius: "6px",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            border: "1px solid #e6e6e9",
            borderTop: 0,
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 500,
            color: "#116b6f",
            backgroundColor: "#e6e7e7",
            height: "33px",
            padding: "5px 12px",
            whiteSpace: "nowrap",
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root": {
            "&:nth-of-type(even)": {
              backgroundColor: "#f7f7f8",
            },
          },
          "& .MuiTableCell-root": {
            color: "#030712",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "20px",
            letterSpacing: "-1%",
            padding: "17px ",
            border: "1px solid #e6e6e9",
          },
          "& .MuiTableRow-root .MuiTableCell-root:first-of-type": {
            borderLeft: 0,
          },
          "& .MuiTableRow-root .MuiTableCell-root:last-of-type": {
            borderRight: 0,
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {},
      },
    },

    MuiSelect: {
      styleOverrides: {
        icon: {
          color: "#8E9494",
          fontSize: 20,
          marginRight: "8px",
        },
      },
      defaultProps: {
        IconComponent: FiChevronDown,
      },
    },
  },

  palette: {
    neutral: {
      50: "#f7f8f8",
      100: "#e6e7e7",
      200: "#d4d7d7",
      300: "#c0c4c4",
      400: "#aeb3b3",
      500: "#8e9494",
      600: "#687071",
      700: "#424d4d",
      800: "#354141",
      900: "#1c292a",
      950: "#031112",
    },
    grey: {
      100: "#84818A",
    },
    action: {
      active: "#6B7280",
      focus: "rgba(55, 65, 81, 0.12)",
      hover: "rgba(55, 65, 81, 0.04)",
      selected: "#34A6B1",
      disabledBackground: "#F1F2F4",
      disabled: "rgba(55, 65, 81, 0.26)",
    },
    background: {
      default: "#f7f8f8",
      paper: "#FFFFFF",
    },
    divider: "#ebebeb",
    primary: {
      main: "#0B494B",
      light: "#33dae0",
      dark: "#093B3D",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#172C57",
      light: "#274B95",
      dark: "#122345",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#27BE69",
      light: "#45D985",
      dark: "#1F9854",
      contrastText: "#000000",
    },
    info: {
      main: "#51C0FF",
      light: "#75CDFF",
      dark: "#0FA8FF",
      contrastText: "#000000",
    },
    warning: {
      main: "#FFBF0F",
      light: "#FFCB3D",
      dark: "#D69D00",
      contrastText: "#000000",
    },
    error: {
      main: "#EA4334",
      light: "#EE695D",
      dark: "#D02516",
      contrastText: "#000000",
    },
    text: {
      primary: "#030712",
      secondary: "#687071",
      disabled: "#899DB7",
    },
  },
  shape: {
    borderRadius: 6,
  },
  shadows: [
    "none",
    "0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)",
    "0px 1px 2px rgba(100, 116, 139, 0.12)",
    "0px 1px 4px rgba(100, 116, 139, 0.12)",
    "0px 1px 5px rgba(100, 116, 139, 0.12)",
    "0px 1px 6px rgba(100, 116, 139, 0.12)",
    "0px 2px 6px rgba(100, 116, 139, 0.12)",
    "0px 3px 6px rgba(100, 116, 139, 0.12)",
    "0px 2px 4px rgba(31, 41, 55, 0.06), 0px 4px 6px rgba(100, 116, 139, 0.12)",
    "0px 5px 12px rgba(100, 116, 139, 0.12)",
    "0px 5px 14px rgba(100, 116, 139, 0.12)",
    "0px 5px 15px rgba(100, 116, 139, 0.12)",
    "0px 6px 15px rgba(100, 116, 139, 0.12)",
    "0px 7px 15px rgba(100, 116, 139, 0.12)",
    "0px 8px 15px rgba(100, 116, 139, 0.12)",
    "0px 9px 15px rgba(100, 116, 139, 0.12)",
    "0px 10px 15px rgba(100, 116, 139, 0.12)",
    "0px 12px 22px -8px rgba(100, 116, 139, 0.25)",
    "0px 13px 22px -8px rgba(100, 116, 139, 0.25)",
    "0px 14px 24px -8px rgba(100, 116, 139, 0.25)",
    "0px 10px 10px rgba(31, 41, 55, 0.04), 0px 20px 25px rgba(31, 41, 55, 0.1)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
    "0px 25px 50px rgba(100, 116, 139, 0.25)",
  ],
  typography: {
    button: {
      fontWeight: 400,
      ...fluidTypography.button,
    },
    fontFamily: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica,
			 Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"`,
    body1: {
      ...fluidTypography.body1,
      fontWeight: 400,
    },
    body2: {
      ...fluidTypography.body2,
      fontWeight: 500,
      color: "#2E2C34",
    },
    subtitle1: {
      ...fluidTypography.subtitle1,
      fontWeight: 500,
    },
    subtitle2: {
      ...fluidTypography.subtitle2,
      fontWeight: 500,
    },
    overline: {
      ...fluidTypography.overline,
      fontWeight: 600,
    },
    caption: {
      ...fluidTypography.caption,
      fontWeight: 400,
    },
    h1: {
      ...fluidTypography.h1,
      fontWeight: 700,
    },
    h2: {
      ...fluidTypography.h2,
      fontWeight: 700,
    },
    h3: {
      ...fluidTypography.h3,
      fontWeight: 600,
    },
    h4: {
      ...fluidTypography.h4,
      fontWeight: 500,
    },
    h5: {
      ...fluidTypography.h5,
      fontWeight: 500,
    },
    h6: {
      ...fluidTypography.h6,
      fontWeight: 500,
    },
  },
});
